import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../api'

const steps = [
  { id: 'welcome', title: 'Welcome to Velumio.' },
  { id: 'what', title: 'Here is what Velumio does.' },
  { id: 'profile', title: 'Tell us a little about yourself.' },
  { id: 'journal', title: 'Write your first entry.' },
  { id: 'ready', title: 'You are all set.' },
]

export default function Onboarding() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [currentStep, setCurrentStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [firstEntry, setFirstEntry] = useState('')
  const [workPattern, setWorkPattern] = useState('')
  const [physicalActivity, setPhysicalActivity] = useState('')
  const [caringResponsibilities, setCaringResponsibilities] = useState('')

  const step = steps[currentStep]
  const isLast = currentStep === steps.length - 1
  const progress = (currentStep / (steps.length - 1)) * 100

  const handleNext = async () => {
    if (saving) return
    setSaving(true)

    try {
      if (step.id === 'profile') {
        await api.post('/profile', {
          work_pattern: workPattern || 'not_specified',
          physical_activity: physicalActivity || null,
          caring_responsibilities: caringResponsibilities || null,
        }).catch(err => console.error('Profile save failed', err))
      }

      if (step.id === 'journal' && firstEntry.trim().length > 0) {
        await api.post('/entries', {
          content: firstEntry,
          input_method: 'text',
          privacy_level: 'private',
          tags: ['first-entry'],
          location_context: null,
        }).catch(err => console.error('Entry save failed', err))
      }

      if (isLast) {
        navigate('/dashboard', { replace: true })
      } else {
        setCurrentStep(prev => prev + 1)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleSkip = () => {
    if (isLast) {
      navigate('/dashboard', { replace: true })
    } else {
      setCurrentStep(prev => prev + 1)
    }
  }

  const optionButton = (
    value: string,
    label: string,
    selected: string,
    setter: (v: string) => void
  ) => (
    <button
      key={value}
      onClick={() => setter(value)}
      style={{
        padding: '9px 16px', fontSize: '13px',
        borderRadius: '9999px', cursor: 'pointer',
        fontFamily: 'var(--font-body)',
        border: selected === value
          ? '2px solid #C8963E'
          : '1.5px solid rgba(248,244,238,0.2)',
        background: selected === value
          ? 'rgba(200,150,62,0.15)' : 'transparent',
        color: selected === value ? '#C8963E' : 'rgba(248,244,238,0.55)',
        fontWeight: selected === value ? 600 : 400,
      }}
    >
      {label}
    </button>
  )

  const questionBlock = (
    question: string,
    options: { value: string; label: string }[],
    selected: string,
    setter: (v: string) => void
  ) => (
    <div style={{ marginBottom: '24px' }}>
      <p style={{
        fontSize: '13px', fontWeight: 600,
        color: 'rgba(248,244,238,0.7)', marginBottom: '10px',
      }}>
        {question}
      </p>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' as const }}>
        {options.map(opt => optionButton(opt.value, opt.label, selected, setter))}
      </div>
    </div>
  )

  return (
    <div style={{
      minHeight: '100vh', background: '#0F1B2D',
      display: 'flex', flexDirection: 'column' as const,
      alignItems: 'center', justifyContent: 'center',
      padding: '80px 24px 40px',
    }}>

      {/* Progress bar */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        height: '3px', background: 'rgba(248,244,238,0.1)',
      }}>
        <div style={{
          height: '100%', width: progress + '%',
          background: '#C8963E', transition: 'width 0.4s ease',
        }} />
      </div>

      {/* Logo */}
      <div style={{
        position: 'fixed', top: '24px', left: '28px',
        fontFamily: 'var(--font-display)', fontSize: '20px',
        fontWeight: 700, color: 'var(--cream)',
      }}>
        Velu<span style={{ color: '#C8963E' }}>mio</span>
      </div>

      {/* Step counter */}
      <p style={{
        position: 'fixed', top: '28px', right: '28px',
        fontSize: '12px', color: 'rgba(248,244,238,0.4)',
      }}>
        {currentStep + 1} of {steps.length}
      </p>

      <div style={{
        maxWidth: '560px', width: '100%', textAlign: 'center',
      }}>

        {/* WELCOME */}
        {step.id === 'welcome' && (
          <>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>👋</div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(26px, 5vw, 38px)',
              color: 'var(--cream)', marginBottom: '16px', fontWeight: 700,
            }}>
              Welcome{user?.display_name ? ', ' + user.display_name : ''}.
            </h1>
            <p style={{
              fontSize: '17px', color: 'rgba(248,244,238,0.6)',
              lineHeight: 1.7, marginBottom: '16px',
            }}>
              You just made a genuinely good decision for yourself.
            </p>
            <p style={{
              fontSize: '15px', color: 'rgba(248,244,238,0.45)', lineHeight: 1.7,
            }}>
              Velumio is a private space to understand how you are really
              doing, not how you think you should be doing. This takes
              about two minutes.
            </p>
          </>
        )}

        {/* WHAT IT DOES */}
        {step.id === 'what' && (
          <>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(22px, 4vw, 32px)',
              color: 'var(--cream)', marginBottom: '10px',
            }}>
              Here is what Velumio does.
            </h1>
            <p style={{
              fontSize: '15px', color: 'rgba(248,244,238,0.5)',
              marginBottom: '32px',
            }}>
              Three things, quietly, in the background.
            </p>
            <div style={{
              display: 'flex', flexDirection: 'column' as const,
              gap: '14px', textAlign: 'left' as const,
            }}>
              {[
                {
                  icon: '🎤',
                  title: 'Listens to you',
                  desc: 'Journal by typing, speaking, or walking and talking. Everything is transcribed and encrypted.',
                },
                {
                  icon: '◈',
                  title: 'Calculates your wellbeing score',
                  desc: 'Your entries, mood, and financial stress combine into a weekly score that reflects your real state.',
                },
                {
                  icon: '✉',
                  title: 'Writes you a personal letter',
                  desc: 'Each week you receive a thoughtful letter based on your entries. Honest, warm, and private.',
                },
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', gap: '16px', padding: '18px',
                  background: 'rgba(248,244,238,0.05)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid rgba(248,244,238,0.1)',
                  alignItems: 'flex-start',
                }}>
                  <span style={{ fontSize: '26px', flexShrink: 0 }}>{item.icon}</span>
                  <div>
                    <p style={{
                      fontSize: '15px', fontWeight: 600,
                      color: 'var(--cream)', marginBottom: '4px',
                    }}>
                      {item.title}
                    </p>
                    <p style={{
                      fontSize: '13px', color: 'rgba(248,244,238,0.5)',
                      lineHeight: 1.6,
                    }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* PROFILE */}
        {step.id === 'profile' && (
          <>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(22px, 4vw, 32px)',
              color: 'var(--cream)', marginBottom: '10px',
            }}>
              Tell us a little about yourself.
            </h1>
            <p style={{
              fontSize: '15px', color: 'rgba(248,244,238,0.5)',
              marginBottom: '32px',
            }}>
              This helps personalise your score. Takes 30 seconds.
            </p>

            <div style={{ textAlign: 'left' as const }}>
              {questionBlock(
                'What is your work pattern?',
                [
                  { value: 'full_time', label: 'Full time' },
                  { value: 'part_time', label: 'Part time' },
                  { value: 'shift_work', label: 'Shift work' },
                  { value: 'self_employed', label: 'Self employed' },
                  { value: 'not_working', label: 'Not working' },
                ],
                workPattern, setWorkPattern
              )}

              {questionBlock(
                'How much physical activity do you get?',
                [
                  { value: 'none', label: 'None' },
                  { value: 'light', label: 'Light' },
                  { value: 'moderate', label: 'Moderate' },
                  { value: 'active', label: 'Very active' },
                ],
                physicalActivity, setPhysicalActivity
              )}

              {questionBlock(
                'Do you have caring responsibilities?',
                [
                  { value: 'none', label: 'None' },
                  { value: 'young_children', label: 'Young children' },
                  { value: 'older_children', label: 'Older children' },
                  { value: 'adult_care', label: 'Caring for an adult' },
                ],
                caringResponsibilities, setCaringResponsibilities
              )}
            </div>
          </>
        )}

        {/* FIRST ENTRY */}
        {step.id === 'journal' && (
          <>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(22px, 4vw, 32px)',
              color: 'var(--cream)', marginBottom: '10px',
            }}>
              Write your first entry.
            </h1>
            <p style={{
              fontSize: '15px', color: 'rgba(248,244,238,0.5)',
              marginBottom: '24px',
            }}>
              Just a few sentences. Anything on your mind right now.
            </p>
            <textarea
              value={firstEntry}
              onChange={e => setFirstEntry(e.target.value)}
              placeholder="How are you feeling today? What has been on your mind lately? There are no wrong answers."
              style={{
                width: '100%', minHeight: '150px', padding: '16px',
                fontSize: '15px', background: 'rgba(248,244,238,0.05)',
                border: '1.5px solid rgba(248,244,238,0.15)',
                borderRadius: 'var(--radius-md)', color: 'var(--cream)',
                outline: 'none', fontFamily: 'var(--font-body)',
                lineHeight: 1.7, resize: 'vertical' as const,
                boxSizing: 'border-box' as const,
              }}
            />
            <p style={{
              fontSize: '12px', color: 'rgba(248,244,238,0.3)',
              marginTop: '8px', textAlign: 'left' as const,
            }}>
              Encrypted and completely private. Only you can read this.
            </p>
          </>
        )}

        {/* READY */}
        {step.id === 'ready' && (
          <>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>✨</div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(26px, 5vw, 38px)',
              color: 'var(--cream)', marginBottom: '16px',
            }}>
              You are all set.
            </h1>
            <p style={{
              fontSize: '17px', color: 'rgba(248,244,238,0.6)',
              lineHeight: 1.7, marginBottom: '16px',
            }}>
              Check back each morning for your Energy Budget, and each
              week for your personal letter.
            </p>
            <p style={{
              fontSize: '14px', color: 'rgba(248,244,238,0.4)',
              fontStyle: 'italic', fontFamily: 'var(--font-display)',
            }}>
              The goal is not to be perfect. The goal is to be aware.
            </p>
          </>
        )}

        {/* NAVIGATION */}
        <div style={{
          display: 'flex', gap: '12px', justifyContent: 'center',
          marginTop: '36px', flexWrap: 'wrap' as const,
        }}>
          <button
            onClick={handleNext}
            disabled={saving}
            style={{
              padding: '14px 38px', fontSize: '16px', fontWeight: 600,
              color: '#0F1B2D', background: '#C8963E', border: 'none',
              borderRadius: '9999px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-body)',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving
              ? 'Saving...'
              : isLast
              ? 'Go to my dashboard'
              : 'Continue'}
          </button>

          {!isLast && step.id !== 'welcome' && (
            <button
              onClick={handleSkip}
              style={{
                padding: '14px 22px', fontSize: '14px',
                color: 'rgba(248,244,238,0.4)', background: 'transparent',
                border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}
            >
              Skip for now
            </button>
          )}
        </div>

      </div>
    </div>
  )
}